import type { FleetJobRow, FleetRosterShiftRow } from "./types";
import { computeJobTimes, timeToHours, trimToHHMM, roundUpToStep, type FleetCapacityByType } from "./utils";

export interface BreakSuggestion {
  shiftId: string;
  driverName: string;
  vehicleNumber: string | null;
  shiftStartHHMM: string;
  shiftEndHHMM: string;
  /** null when no valid gap could be found -- needs a manual break time. */
  suggestedStartHHMM: string | null;
  suggestedEndHHMM: string | null;
  reason?: string;
}

interface Interval {
  startH: number;
  endH: number;
}

function hoursToHHMM(h: number): string {
  const normalised = ((h % 24) + 24) % 24;
  const totalMinutes = Math.round(normalised * 60);
  const hh = Math.floor(totalMinutes / 60) % 24;
  const mm = totalMinutes % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

/** Free windows within [rangeStart, rangeEnd] once the given busy intervals are cut out. */
function freeGaps(busy: Interval[], rangeStart: number, rangeEnd: number): Interval[] {
  const sorted = [...busy].sort((a, b) => a.startH - b.startH);
  const gaps: Interval[] = [];
  let cursor = rangeStart;
  for (const b of sorted) {
    if (b.startH > cursor) gaps.push({ startH: cursor, endH: Math.min(b.startH, rangeEnd) });
    cursor = Math.max(cursor, b.endH);
    if (cursor >= rangeEnd) break;
  }
  if (cursor < rangeEnd) gaps.push({ startH: cursor, endH: rangeEnd });
  return gaps.filter((g) => g.endH - g.startH > 1e-9);
}

function overlapHours(aStart: number, aEnd: number, bStart: number, bEnd: number): number {
  return Math.max(0, Math.min(aEnd, bEnd) - Math.max(aStart, bStart));
}

/**
 * Checks a candidate [start, start+durationH] window against every
 * already-placed break. `valid` is false if it overlaps ANY single one of
 * them by more than maxOverlapH -- that's the hard cap that keeps coverage
 * from being compromised, since two drivers can be off at once for a bit
 * without the fleet actually losing a vehicle for a full break window.
 * `totalOverlapH` (summed across all placed breaks) is the staggering
 * signal used to prefer the least-overlapping valid option.
 */
function evaluateCandidate(
  candidateStart: number,
  durationH: number,
  placed: Interval[],
  maxOverlapH: number
): { valid: boolean; totalOverlapH: number } {
  const candidateEnd = candidateStart + durationH;
  let totalOverlapH = 0;
  for (const p of placed) {
    const ov = overlapHours(candidateStart, candidateEnd, p.startH, p.endH);
    if (ov > maxOverlapH + 1e-9) return { valid: false, totalOverlapH };
    totalOverlapH += ov;
  }
  return { valid: true, totalOverlapH };
}

// A gap's own start is whatever time a job happened to end (e.g. 7:43), not
// a clean number -- stepping from there would suggest break times nobody
// wants to text a driver. Every candidate is snapped to a 15-minute mark
// (on the hour, :15, :30, :45).
const CANDIDATE_STEP_H = 15 / 60;

/**
 * Suggests a break time for every driving shift long enough to need one,
 * per NZ work-time rules: a break must START before `maxHoursBeforeBreak`
 * hours of continuous on-duty time have passed (it can still be running
 * once that mark is reached). Breaks are placed in a genuine gap in that
 * vehicle's own bookings, targeting the middle of the driver's shift so it
 * actually splits their day in two (not just the first clear slot, which
 * tends to land right at shift start before any job is booked yet), then
 * nudged toward whichever valid time overlaps other drivers' breaks least.
 *
 * `allowOverlap`/`overlapPercent` control how strictly "at the same time"
 * is enforced: with overlap off, two breaks may not share any time at all;
 * with it on, they may share up to `overlapPercent`% of a single break's
 * length (e.g. 50% of a 45 min break = 22 min) before that slot is rejected
 * -- a full-length overlap would mean two drivers off at once, which is
 * what actually costs coverage; a partial one doesn't.
 */
export function generateBreakSuggestions(
  dataByType: Record<string, FleetCapacityByType>,
  dateStr: string,
  maxHoursBeforeBreak: number,
  breakMinutes: number,
  allowOverlap = false,
  overlapPercent = 50
): BreakSuggestion[] {
  const durationH = breakMinutes / 60;
  const maxOverlapH = allowOverlap ? Math.round((breakMinutes * overlapPercent) / 100) / 60 : 0;

  type Candidate = {
    shift: FleetRosterShiftRow;
    startH: number;
    endH: number;
    windowEnd: number;
    gaps: Interval[];
  };

  const candidates: Candidate[] = [];

  for (const { shifts, jobs } of Object.values(dataByType)) {
    for (const shift of shifts) {
      if (shift.shift_date !== dateStr) continue;
      if (!shift.vehicle_number) continue;

      const startH = timeToHours(trimToHHMM(shift.start_hhmm));
      let endH = timeToHours(trimToHHMM(shift.end_hhmm));
      if (endH <= startH) endH += 24; // overnight shift

      const shiftDurationH = endH - startH;
      if (shiftDurationH <= maxHoursBeforeBreak) continue; // under the threshold, no break required

      // Break must START by the cutoff, and still fully fit before shift end.
      const windowEnd = Math.min(startH + maxHoursBeforeBreak, endH - durationH);
      if (windowEnd <= startH) continue; // shift barely clears the threshold, no room to fit the break

      const busy: Interval[] = jobs
        .filter((j: FleetJobRow) => j.vehicle_number === shift.vehicle_number && j.pu_date === dateStr)
        .map((j) => computeJobTimes(j))
        .map(({ startH: s, endH: e }) => ({ startH: s, endH: e }));

      const gaps = freeGaps(busy, startH, windowEnd).filter((g) => g.endH - g.startH >= durationH - 1e-9);

      candidates.push({ shift, startH, endH, windowEnd, gaps });
    }
  }

  // Most-constrained drivers (least total gap time) choose first, so a
  // driver with only one workable window isn't crowded out by someone with
  // a wide-open day.
  candidates.sort((a, b) => {
    const totalA = a.gaps.reduce((sum, g) => sum + (g.endH - g.startH), 0);
    const totalB = b.gaps.reduce((sum, g) => sum + (g.endH - g.startH), 0);
    return totalA - totalB;
  });

  const placed: Interval[] = [];
  const results: BreakSuggestion[] = [];

  for (const c of candidates) {
    const base = {
      shiftId: c.shift.id,
      driverName: c.shift.driver_name,
      vehicleNumber: c.shift.vehicle_number,
      shiftStartHHMM: trimToHHMM(c.shift.start_hhmm),
      shiftEndHHMM: trimToHHMM(c.shift.end_hhmm),
    };

    if (c.gaps.length === 0) {
      results.push({
        ...base,
        suggestedStartHHMM: null,
        suggestedEndHHMM: null,
        reason: "No gap in this vehicle's bookings is long enough before the break cutoff -- needs a manual break time.",
      });
      continue;
    }

    // Aim for the middle of the driver's actual shift, not just the first
    // slot that clears the overlap check -- scanning from the window's
    // start regularly landed the break right at shift start, before any
    // job had even been booked against the vehicle yet, which defeats the
    // point of a break splitting the day in two.
    const fullShiftDurationH = c.endH - c.startH;
    const idealCenter = c.startH + fullShiftDurationH * 0.5;
    const idealStart = Math.min(Math.max(idealCenter - durationH / 2, c.startH), c.windowEnd - durationH);

    let bestStart: number | null = null;
    let bestDistanceH = Infinity;
    let bestOverlapH = Infinity;
    for (const g of c.gaps) {
      const firstCleanT = roundUpToStep(g.startH, CANDIDATE_STEP_H);
      for (let t = firstCleanT; t <= g.endH - durationH + 1e-9; t += CANDIDATE_STEP_H) {
        const { valid, totalOverlapH } = evaluateCandidate(t, durationH, placed, maxOverlapH);
        if (!valid) continue;
        const distanceH = Math.abs(t - idealStart);
        if (distanceH < bestDistanceH - 1e-9 || (Math.abs(distanceH - bestDistanceH) <= 1e-9 && totalOverlapH < bestOverlapH)) {
          bestDistanceH = distanceH;
          bestOverlapH = totalOverlapH;
          bestStart = t;
        }
      }
    }

    if (bestStart === null) {
      const overlapNote = allowOverlap
        ? ` even allowing up to ${Math.round(maxOverlapH * 60)} min overlap with another driver's break`
        : "";
      results.push({
        ...base,
        suggestedStartHHMM: null,
        suggestedEndHHMM: null,
        reason: `No time in this vehicle's available window avoids clashing with another driver's break${overlapNote} -- needs a manual break time.`,
      });
      continue;
    }

    placed.push({ startH: bestStart, endH: bestStart + durationH });
    results.push({
      ...base,
      suggestedStartHHMM: hoursToHHMM(bestStart),
      suggestedEndHHMM: hoursToHHMM(bestStart + durationH),
    });
  }

  // Restore a stable, readable order for display (by shift start time).
  results.sort((a, b) => a.shiftStartHHMM.localeCompare(b.shiftStartHHMM));
  return results;
}

export interface PublishedBreakInput {
  id: string;
  shiftId: string;
  vehicleNumber: string | null;
  /** Resolved break start -- confirmed_start_hhmm if set, else suggested_start_hhmm. */
  startHHMM: string;
}

export interface BreakConflictResolution {
  id: string;
  shiftId: string;
  /** True when a genuine free gap was found and the break should move there. */
  canPush: boolean;
  newStartHHMM: string | null;
  newEndHHMM: string | null;
}

/**
 * Checks every published break against the vehicle's REAL current bookings
 * -- TaxiCaller dispatch doesn't know or care about a scheduled break, so a
 * real job can land right on top of one after it's already been published.
 * For each one that's now blocked:
 *  - if a genuine free gap still exists before the max-hours-before-break
 *    cutoff, it can be pushed there automatically;
 *  - if not (the driver's already at or past the point where policy
 *    requires a break, with no room left), it can't be moved further -- the
 *    caller should flag it for a dispatcher to resolve by hand instead of
 *    silently leaving a break sitting on top of a real job.
 * Breaks with no conflict at all aren't included in the result.
 */
export function checkPublishedBreakConflicts(
  publishedBreaks: PublishedBreakInput[],
  dataByType: Record<string, FleetCapacityByType>,
  dateStr: string,
  maxHoursBeforeBreak: number,
  breakMinutes: number
): BreakConflictResolution[] {
  const durationH = breakMinutes / 60;
  const results: BreakConflictResolution[] = [];

  // Pooled across every vehicle type's data, since a given break's vehicle
  // could be in any of them.
  const shiftById = new Map<string, { startH: number; endH: number }>();
  const jobsByVehicle = new Map<string, Interval[]>();
  for (const { shifts, jobs } of Object.values(dataByType)) {
    for (const shift of shifts) {
      if (shift.shift_date !== dateStr || !shift.vehicle_number) continue;
      const startH = timeToHours(trimToHHMM(shift.start_hhmm));
      let endH = timeToHours(trimToHHMM(shift.end_hhmm));
      if (endH <= startH) endH += 24;
      shiftById.set(shift.id, { startH, endH });
    }
    for (const job of jobs) {
      if (job.pu_date !== dateStr || !job.vehicle_number) continue;
      const { startH, endH } = computeJobTimes(job);
      if (!jobsByVehicle.has(job.vehicle_number)) jobsByVehicle.set(job.vehicle_number, []);
      jobsByVehicle.get(job.vehicle_number)!.push({ startH, endH });
    }
  }

  for (const brk of publishedBreaks) {
    const shift = shiftById.get(brk.shiftId);
    if (!shift || !brk.vehicleNumber) continue;

    const busy = jobsByVehicle.get(brk.vehicleNumber) || [];
    const curStart = timeToHours(trimToHHMM(brk.startHHMM));
    const curEnd = curStart + durationH;

    const conflicted = busy.some((b) => overlapHours(curStart, curEnd, b.startH, b.endH) > 1e-9);
    if (!conflicted) continue;

    const windowEnd = Math.min(shift.startH + maxHoursBeforeBreak, shift.endH - durationH);
    const gaps = freeGaps(busy, curStart, windowEnd).filter((g) => g.endH - g.startH >= durationH - 1e-9);

    if (gaps.length === 0) {
      results.push({ id: brk.id, shiftId: brk.shiftId, canPush: false, newStartHHMM: null, newEndHHMM: null });
      continue;
    }

    // Snap to a clean mark where possible, but never past the gap the
    // rounding would otherwise push it out of.
    const rounded = roundUpToStep(gaps[0].startH, CANDIDATE_STEP_H);
    const newStart = rounded + durationH <= gaps[0].endH + 1e-9 ? rounded : gaps[0].startH;
    results.push({
      id: brk.id,
      shiftId: brk.shiftId,
      canPush: true,
      newStartHHMM: hoursToHHMM(newStart),
      newEndHHMM: hoursToHHMM(newStart + durationH),
    });
  }

  return results;
}
